/**
 * Native full-screen for one element of the stage — today, the shared screen.
 *
 * The element, not the document: full-screening the whole page would take the
 * meeting bar and controls with it, and the point of the button is to give the
 * SHARE every pixel. State is read back from `fullscreenchange` so pressing
 * Escape (which never reaches React) still turns the button back.
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export interface UseFullscreenResult {
  /** Attach to the element that should go full-screen. */
  ref: RefObject<HTMLDivElement>;
  isFullscreen: boolean;
  /** False in an iframe without `allowfullscreen`, or on an unsupported browser. */
  supported: boolean;
  toggle: () => void;
}

export function useFullscreen(): UseFullscreenResult {
  const ref = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [supported] = useState(
    () => typeof document !== 'undefined' && Boolean(document.fullscreenEnabled),
  );

  useEffect(() => {
    const onChange = (): void => setIsFullscreen(document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggle = useCallback(() => {
    const node = ref.current;
    if (!node) return;
    if (document.fullscreenElement === node) {
      void document.exitFullscreen().catch(() => undefined);
      return;
    }
    void node.requestFullscreen().catch(() => undefined);
  }, []);

  return { ref, isFullscreen, supported, toggle };
}
